import api from '../../api'

// Utilities
import isEmpty from '../../utility/isEmpty'
import Logger from '../../utility/loggerUtil'
import sessionRecord from '../../utility/sessionRecordUtil'

import { CognitoConstants } from '../../constants.js'
import moment from 'moment'

export default {
  async getCustomerId (context, data = {}) {
    try {
      let customerId
      let customerIds

      const results = await Promise.all([
        isEmpty(data.attributes) ? api.user.getAttributes() : undefined, // 0
        isEmpty(data.lmsReferenceIds) ? context.dispatch('getLmsReferenceIds') : undefined // 1
      ]).then(data => {
        return {
          attributes: data[0],
          lmsReferenceIds: data[1]
        }
      })

      if (!isEmpty(results.attributes)) {
        data.attributes = results.attributes
      }

      if (!isEmpty(results.lmsReferenceIds)) {
        data.lmsReferenceIds = results.lmsReferenceIds
      }

      const customerIdsAttribute = data.attributes.find(attr => attr.getName() === CognitoConstants.lmsCustomerIdsAttribute)
      const mdmIdAttribute = data.attributes.find(attr => attr.getName() === CognitoConstants.mdmIdAttribute)
      const mdmId = isEmpty(mdmIdAttribute) ? undefined : mdmIdAttribute.getValue()

      if (!isEmpty(customerIdsAttribute)) {
        customerIds = JSON.parse(customerIdsAttribute.getValue())
        customerId = customerIds[1]
      }

      context.commit('setCustomerId', { customerId })
      context.commit('setCustomerIds', { customerIds, mdmId })
      context.commit('setMdmId', mdmId)
      context.commit('referral/setReferralStatus', data.attributes, { root: true })

      return {
        attributes: data.attributes,
        lmsReferenceIds: data.lmsReferenceIds,
        customerIds,
        mdmId
      }
    } catch (e) {
      Logger.error('Error getting customer ID')
      Logger.debug(e)
      throw e
    }
  },

  async getCustomerInfo (context, data = {}) {
    try {
      if (isEmpty(context.state.customerIds)) {
        await context.dispatch('getCustomerId')
      }

      let lmsId = data.lmsId
      if (isEmpty(lmsId)) {
        lmsId = context.rootGetters['loans/getActiveLms']
      }

      const customer = await api.customer.getCustomerInfo(lmsId, data.loanHeader, data.storeId).catch(async (error) => {
        Logger.warn('Failed to get cosigner details.')
        Logger.debug(error)
        return api.customer.getCustomerInfo(lmsId, data.loanHeader, data.storeId)
      })

      // For CoSigner Customer Info
      if (!isEmpty(data.loanId)) {
        return customer
      }

      Logger.info('Got Customer details')
      Logger.debug(customer)

      if (!isEmpty(customer)) {
        customer.addresses = context.getters.processAddresses(customer.addresses)

        const data = {
          customerName: customer.customerName,
          fullCustomerName: customer.fullCustomerName,
          customerFirstName: customer.customerFirstName,
          customerLastName: customer.customerLastName,
          dob: moment(customer.dob, 'MM/DD/YYYY'),
          displaySsn: customer.displaySsn,
          phoneNumber: customer.phoneNumber,
          addresses: customer.addresses,
          customerStoreId: customer.storeId,
          incomeList: customer.incomeList,
          lastPayDate: customer.lastPayDate,
          payFrequency: customer.payFrequency,
          email: customer.email,
          discretionaryIncome: customer.discretionaryIncome,
          communicationSettings: customer.communicationPreferences,
          canUpdateUserLogin: customer.canUpdateUserLogin,
          hasVerifiedProfileEmail: customer.hasVerifiedProfileEmail,
          hasVerifiedProfilePhone: customer.hasVerifiedProfilePhone
        }

        if (!isEmpty(customer.banks)) {
          data.banks = customer.banks
        } else {
          data.banks = [] // Clear bank info if already saved
        }

        context.commit('setCustomerData', data)
        context.commit('referral/setReferralInfo', customer, { root: true })

        sessionRecord.updateIdentity() // call after the customer data has been refreshed, above
      }

      return customer
    } catch (e) {
      Logger.error('Error getting customer info')
      Logger.debug(e)
      throw e
    }
  },

  async getLmsReferenceIds (context) {
    try {
      const referenceIds = (await api.config.getLmsReferenceIds()).referenceIds

      Logger.info('Got LMS Reference Ids')
      Logger.debug(referenceIds)

      if (!isEmpty(referenceIds)) {
        context.commit('setReferenceIds', referenceIds)
      }

      return referenceIds
    } catch (e) {
      Logger.error('Error getting customer reference IDs')
      Logger.debug(e)
      throw e
    }
  },

  async updateCommunicationSettings (context, data) {
    try {
      const customerId = data.customerId
      const communicationSettings = data.communicationSettings
      // loanId and storeId should only be passed in if updating comm preferences for an alternate party
      const loanHeader = data.loanHeader
      const storeId = data.storeId
      const updatedSettings = await api.customer.updateCommunicationSettings(customerId, communicationSettings, loanHeader, storeId)

      if (!isEmpty(updatedSettings)) {
        context.commit('setCustomerData', { communicationSettings: updatedSettings.communicationPreferences })
      }
    } catch (e) {
      Logger.error('Error updating customer info')
      Logger.debug(e)
      throw e
    }
  },

  async experianSSNCheck (context, data) {
    try {
      await api.customer.experianSSNCheck(data.customerId, data.loanHeader, data.loanId, data.productId, data.storeId)
    } catch (e) {
      Logger.error('Error running experian ssn check')
      Logger.debug(e)
    }
  },

  /**
   * Pulls the latest mdmId using linked customer information and saves to Vuex.
   */
  async updateMdmId (context) {
    try {
      await api.user.updateMdmProfile().catch(e => {
        Logger.error('Error getting mdmId.')
        Logger.debug(e)
      })

      const attributes = await api.user.getAttributes()
      const mdmIdAttribute = attributes.find(attr => attr.getName() === CognitoConstants.mdmIdAttribute)
      const mdmId = isEmpty(mdmIdAttribute) ? undefined : mdmIdAttribute.getValue()

      context.commit('setMdmId', mdmId)
    } catch (e) {
      Logger.error('Error updating and getting mdmId')
      Logger.debug(e)
    }
  },

  async getBankInfo (context, data) {
    try {
      const routingNumber = data.routingNumber

      return await api.customer.getBankInfo(routingNumber)
    } catch (e) {
      Logger.error('Error getting bank info')
      Logger.debug(e)
      throw e
    }
  },

  async updateBankingInfo (context, data) {
    try {
      const customerId = context.customerId
      const bankName = data.bankName
      const routingNumber = data.routingNumber
      const accountNumber = data.accountNumber

      const updateResponse = await api.customer.updateBankingInfo(customerId, bankName, routingNumber, accountNumber)

      context.commit('addBank', {
        id: updateResponse.bankAccountId,
        bankName,
        routingNumber,
        accountNumber,
        primary: false
      })

      return updateResponse
    } catch (e) {
      Logger.error('Error updating banking info')
      Logger.debug(e)
      throw e
    }
  },

  async guestLookup (context, data) {
    try {
      const response = await api.customer.guestLookup(data.birthDate, data.lastFourOfSSN, data.customerReferralId)

      return response
    } catch (e) {
      Logger.error('Error on customer lookup')
      Logger.debug(e)
      throw e
    }
  }
}
